import {
  DEVICE_STORE_EVENT,
  type DeviceStateStore,
  type DeviceStoreEvent,
  type DeviceStoreValue,
} from "@production-hub/shared";

/**
 * Live device stores of one bridge from its `GET /api/events` stream
 * (docs/device-state-deltas.md): one EventSource per bridge URL, shared by
 * every hook that shows a device of it and closed shortly after the last
 * one unsubscribes.
 *
 * A bridge without the endpoint (older than the stream) makes the
 * EventSource fail before it ever opens: the mode becomes `unsupported` and
 * the hooks poll the full store instead. The stream is tried again every
 * minute, so a bridge updated underneath switches over.
 */

export type BridgeStreamMode =
  | "connecting"
  | "open"
  /** Lost or not reachable; the EventSource keeps retrying */
  | "reconnecting"
  /** No events endpoint (old bridge): poll the full store */
  | "unsupported";

export interface DeviceStoreView {
  mode: BridgeStreamMode;
  /** Store of the device; undefined until its snapshot arrived */
  data: DeviceStateStore | undefined;
  /** The bridge reports no state (device not connected) */
  unavailable: boolean;
}

interface PendingWrite {
  value: DeviceStoreValue;
  /** Latest value the bridge reported for the path (restored on drop) */
  server: DeviceStoreValue | undefined;
  seq: number;
  timer?: ReturnType<typeof setTimeout>;
}

interface DeviceEntry {
  epoch: string | null;
  revision: number;
  /**
   * Bridge values with the optimistic writes applied. Updated in place (a
   * copy of ~10k values per change costs ~5 ms); every change publishes a
   * new `DeviceStateStore` wrapper around it, see `storeChanges`.
   */
  values: Record<string, DeviceStoreValue> | null;
  /** Paths changed since the last publish; null = everything (snapshot) */
  changed: Set<string> | null;
  received: boolean;
  /** Optimistic writes, by path */
  pending: Map<string, PendingWrite>;
  view: DeviceStoreView;
  listeners: Set<() => void>;
}

/** Minimal EventSource surface (injectable for tests) */
export interface EventSourceLike {
  readonly readyState: number;
  onopen: ((event: Event) => void) | null;
  onerror: ((event: Event) => void) | null;
  addEventListener(
    type: string,
    listener: (event: MessageEvent<string>) => void,
  ): void;
  close(): void;
}

export interface BridgeStoreEventsOptions {
  createEventSource?: (url: string) => EventSourceLike;
  /** Deltas arriving within this window are published together */
  flushMs?: number;
  /** An optimistic value stays this long after its write was acknowledged */
  settleMs?: number;
  /** Close the stream this long after the last subscriber left */
  idleCloseMs?: number;
  /** Try the stream again after `unsupported` */
  unsupportedRetryMs?: number;
}

const CLOSED = 2;
const NO_VIEW: DeviceStoreView = {
  mode: "connecting",
  data: undefined,
  unavailable: false,
};

/** What changed from the previously published store of the same device */
export interface StoreChanges {
  previous: DeviceStateStore | undefined;
  paths: ReadonlySet<string>;
}
const changesByStore = new WeakMap<DeviceStateStore, StoreChanges>();

/**
 * The paths that changed between the previous store a device published and
 * `store` (undefined for snapshots and stores from elsewhere). The values
 * object is shared and updated in place, so consumers that derive state
 * from a store use this rather than comparing values.
 */
export function storeChanges(
  store: DeviceStateStore,
): StoreChanges | undefined {
  return changesByStore.get(store);
}

export class BridgeStoreEvents {
  private entries = new Map<string, DeviceEntry>();
  private source: EventSourceLike | null = null;
  private opened = false;
  private mode: BridgeStreamMode = "connecting";
  private subscribers = 0;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private dirty = new Set<DeviceEntry>();
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private seq = 0;
  private readonly options: Required<BridgeStoreEventsOptions>;

  constructor(
    private readonly bridgeUrl: string,
    options: BridgeStoreEventsOptions = {},
  ) {
    this.options = {
      createEventSource: (url) => new EventSource(url),
      flushMs: 30,
      settleMs: 1000,
      idleCloseMs: 5000,
      unsupportedRetryMs: 60_000,
      ...options,
    };
  }

  /** Follow one device; the stream is open while anyone follows. */
  subscribe(id: string, listener: () => void): () => void {
    const entry = this.entry(id);
    entry.listeners.add(listener);
    this.subscribers++;
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.idleTimer = null;
    if (!this.source && !this.retryTimer) this.open();
    return () => {
      entry.listeners.delete(listener);
      if (--this.subscribers > 0) return;
      this.idleTimer = setTimeout(() => {
        this.idleTimer = null;
        this.shutdown();
      }, this.options.idleCloseMs);
    };
  }

  /** Current view of a device; the same object until something changes */
  view(id: string): DeviceStoreView {
    return this.entry(id).view;
  }

  /**
   * Show a written value before the bridge confirms it. Returns a token for
   * `settle`; the optimistic value stays until the bridge reports it, or
   * `settleMs` after a successful write (the bridge's value shows then,
   * e.g. a clamped value), or at once when the write failed.
   */
  write(id: string, path: string, value: DeviceStoreValue): number {
    const entry = this.entry(id);
    const seq = ++this.seq;
    if (!entry.values) return seq;
    const previous = entry.pending.get(path);
    if (previous?.timer) clearTimeout(previous.timer);
    entry.pending.set(path, {
      value,
      server: previous ? previous.server : entry.values[path],
      seq,
    });
    entry.values[path] = value;
    this.touch(entry, path);
    this.publish(entry);
    return seq;
  }

  settle(id: string, path: string, token: number, ok: boolean): void {
    const entry = this.entries.get(id);
    const pending = entry?.pending.get(path);
    if (!entry || !pending || pending.seq !== token) return;
    if (!ok) {
      this.drop(entry, path, pending);
      this.publish(entry);
      return;
    }
    pending.timer = setTimeout(() => {
      if (entry.pending.get(path) !== pending) return;
      this.drop(entry, path, pending);
      this.publish(entry);
    }, this.options.settleMs);
  }

  /** Forget an optimistic value: the bridge's shows again */
  private drop(entry: DeviceEntry, path: string, pending: PendingWrite) {
    if (pending.timer) clearTimeout(pending.timer);
    entry.pending.delete(path);
    if (!entry.values) return;
    if (pending.server === undefined) delete entry.values[path];
    else entry.values[path] = pending.server;
    this.touch(entry, path);
  }

  /** Apply one stream event (exposed for tests) */
  handle(event: DeviceStoreEvent): void {
    const entry = this.entry(event.id);
    if (event.type === "snapshot") {
      entry.epoch = event.epoch;
      entry.revision = event.revision;
      entry.values = event.values;
      entry.received = true;
      entry.changed = null;
      for (const [path, pending] of entry.pending) {
        if (!entry.values) break;
        this.report(entry, path, entry.values[path], pending);
      }
      this.dirty.delete(entry);
      this.publish(entry);
      return;
    }
    if (event.epoch !== entry.epoch || !entry.values) return this.resync();
    if (event.revision <= entry.revision) return; // already applied
    if (event.revision !== entry.revision + 1) return this.resync();
    entry.revision = event.revision;
    for (const [path, value] of event.changes) this.report(entry, path, value);
    for (const path of event.removed) this.report(entry, path, undefined);
    this.dirty.add(entry);
    this.flushTimer ??= setTimeout(() => this.flush(), this.options.flushMs);
  }

  /** The bridge reports a value; an optimistic write keeps showing until it matches */
  private report(
    entry: DeviceEntry,
    path: string,
    value: DeviceStoreValue | undefined,
    pending = entry.pending.get(path),
  ) {
    const values = entry.values!;
    if (pending) {
      pending.server = value;
      if (value !== pending.value) {
        values[path] = pending.value;
        return;
      }
      if (pending.timer) clearTimeout(pending.timer);
      entry.pending.delete(path);
    }
    if (value === undefined) delete values[path];
    else values[path] = value;
    this.touch(entry, path);
  }

  private touch(entry: DeviceEntry, path: string) {
    entry.changed?.add(path);
  }

  private flush(): void {
    this.flushTimer = null;
    const entries = [...this.dirty];
    this.dirty.clear();
    for (const entry of entries) this.publish(entry);
  }

  private entry(id: string): DeviceEntry {
    let entry = this.entries.get(id);
    if (!entry) {
      entry = {
        epoch: null,
        revision: 0,
        values: null,
        changed: new Set(),
        received: false,
        pending: new Map(),
        view: { ...NO_VIEW, mode: this.mode },
        listeners: new Set(),
      };
      this.entries.set(id, entry);
    }
    return entry;
  }

  /** Recompute the view of a device and tell its listeners */
  private publish(entry: DeviceEntry): void {
    const view = entry.view;
    let data = view.data;
    if (!entry.values) {
      data = undefined;
    } else if (
      !data ||
      data.values !== entry.values ||
      entry.changed === null ||
      entry.changed.size > 0
    ) {
      data = { values: entry.values };
      if (view.data && entry.changed) {
        changesByStore.set(data, {
          previous: view.data,
          paths: entry.changed,
        });
      }
    }
    entry.changed = new Set();
    const unavailable = entry.received && !entry.values;
    if (
      view.data === data &&
      view.unavailable === unavailable &&
      view.mode === this.mode
    )
      return;
    entry.view = { mode: this.mode, data, unavailable };
    for (const listener of entry.listeners) listener();
  }

  private setMode(mode: BridgeStreamMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    for (const entry of this.entries.values()) this.publish(entry);
  }

  private open(): void {
    const source = this.options.createEventSource(
      `${this.bridgeUrl}/api/events`,
    );
    this.source = source;
    this.opened = false;
    source.onopen = () => {
      this.opened = true;
      this.setMode("open");
    };
    source.addEventListener(DEVICE_STORE_EVENT, (message) => {
      try {
        this.handle(JSON.parse(message.data) as DeviceStoreEvent);
      } catch (error) {
        console.error("[BridgeStoreEvents] Bad event:", error);
      }
    });
    source.onerror = () => {
      if (this.source !== source) return;
      if (source.readyState !== CLOSED) {
        // The EventSource reconnects by itself and gets fresh snapshots
        this.setMode("reconnecting");
        return;
      }
      source.close();
      this.source = null;
      if (!this.opened) {
        // Refused outright (404): a bridge without the stream
        this.setMode("unsupported");
        this.retryLater(this.options.unsupportedRetryMs);
      } else {
        this.setMode("reconnecting");
        this.retryLater(2000);
      }
    };
  }

  private retryLater(ms: number): void {
    if (this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.subscribers > 0 && !this.source) this.open();
    }, ms);
  }

  /** Reopen the stream: the bridge sends fresh snapshots */
  private resync(): void {
    this.source?.close();
    this.source = null;
    this.open();
  }

  private shutdown(): void {
    this.source?.close();
    this.source = null;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.retryTimer = null;
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
    this.dirty.clear();
    // Kept entries would show stale values until the next snapshots
    for (const [id, entry] of this.entries) {
      if (entry.listeners.size === 0) this.entries.delete(id);
    }
    this.mode = "connecting";
  }
}

const bridges = new Map<string, BridgeStoreEvents>();

/** The shared store stream of a bridge */
export function getBridgeStoreEvents(bridgeUrl: string): BridgeStoreEvents {
  let events = bridges.get(bridgeUrl);
  if (!events)
    bridges.set(bridgeUrl, (events = new BridgeStoreEvents(bridgeUrl)));
  return events;
}
